import { FloorPlanDxfEntity } from '@/editor/entities/floorPlanDxfEntity';
import { FloorPlanPdfEntity } from '@/editor/entities/floorPlanPdfEntity';
import { IDXFRenderContext } from '@/editor/floor-plan/dxf/types';
import { PDFFloorPlanPreviewScene } from '@/editor/floor-plan/pdf/preview';
import EventBus from '@/editor/utils/EventBus';
import pdfJsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Box3 } from 'three';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { DxfBasemapParserService } from '@/editor/floor-plan/dxf/parser-next';
import { globalStores } from '@/editor/stores';

export enum EFloorPlanEventName {
  NOTIFY_EXIST_UPDATE,
  NOTIFY_LAYER_UPDATE,
  LAYER_CLICK
}

export type TFloorPlanEvent = {
  [EFloorPlanEventName.NOTIFY_EXIST_UPDATE]: () => void;
  [EFloorPlanEventName.NOTIFY_LAYER_UPDATE]: () => void;
  [EFloorPlanEventName.LAYER_CLICK]: (layerName: string) => void;
};

export class FloorPlanManager {
  private _event = new EventBus<TFloorPlanEvent>();
  private _entity: FloorPlanDxfEntity | FloorPlanPdfEntity | null = null;
  private _adjustScene: PDFFloorPlanPreviewScene | null = null;

  private _existing: boolean = false;
  private _layerList: { name: string; visible: boolean }[] = [];
  private _hoverLayer: string | null = null;

  get entity() {
    return this._entity || { previewCanvasEl: null, floorPlanType: null };
  }

  public get existing() {
    return this._existing;
  }

  public get layerList() {
    return this._layerList;
  }

  public get hoveredLayer() {
    return this._hoverLayer;
  }

  public renderDXFWithContext = async (context: IDXFRenderContext, hiddenLayers: string[] = []) => {
    this.remove();

    const availableLayerNames = new Set(Object.keys(context.layerLineIndices));
    const restoredHiddenLayers = hiddenLayers.filter((name) => availableLayerNames.has(name));

    if (!this._entity) this._entity = new FloorPlanDxfEntity();
    const entity = this._entity as FloorPlanDxfEntity;
    entity.setContext(context);
    await entity.render();

    this._setExisting(true);
    this._setLayers(
      Object.keys(context.layerLineIndices)
        .map((name) => ({ name, visible: !restoredHiddenLayers.includes(name) }))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    if (restoredHiddenLayers.length > 0) {
      entity.setLayersVisible(Object.fromEntries(restoredHiddenLayers.map((name) => [name, false])));
    }
  };

  /**
   * 底图的增删
   *
   * 这是「导入 / 移除底图」四条路径唯一的汇聚点，显隐开关的复位由 `VisibleController`
   * 订阅 `NOTIFY_EXIST_UPDATE` 完成 —— 开关归 store 所有，manager 不写它
   */
  private _setExisting(existing: boolean) {
    if (this._existing !== existing) {
      this._existing = existing;
      this._event.emit(EFloorPlanEventName.NOTIFY_EXIST_UPDATE);
    }
  }

  private _setLayers(layers: { name: string; visible: boolean }[]) {
    this._layerList = layers;
    this._event.emit(EFloorPlanEventName.NOTIFY_LAYER_UPDATE);
  }

  /**
   * 把显隐施加到底图 entity
   *
   * 只负责渲染，开关的真源是 `VisibilityStore`，唯一写入方是 `VisibleOperation.setFloorPlanVisible`。
   * entity 重建或 store 被重置后需要重新调用一次
   */
  public applyVisible(visible: boolean) {
    if (this._entity && this._entity.getVisible() !== visible) {
      this._entity.setVisible(visible);
    }
  }

  public renderDXF = async (file: File) => {
    this.remove();

    const { renderContext: context } = await DxfBasemapParserService.instance.parseRaw(await file.arrayBuffer(), {
      transformOcs: true,
      renderSolid: true
    });

    if (!this._entity) this._entity = new FloorPlanDxfEntity();
    const entity = this._entity as FloorPlanDxfEntity;
    entity.setContext(context);
    await entity.render();

    this._setExisting(true);
    this._setLayers(
      Object.keys(context.layerLineIndices)
        .map((name) => ({ name, visible: true }))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  public renderPDF = async (file: File, scale?: number) => {
    this.remove();

    if (!this._entity) {
      this._entity = new FloorPlanPdfEntity();
    }

    const canvas = await this.getPdfCanvas(file);
    let scaleValue = scale;
    if (scaleValue === undefined) {
      if (!this._adjustScene) return;
      const canvasRenderScale = canvas.dataset.pdfRenderScale ? parseFloat(canvas.dataset.pdfRenderScale) : 1;
      const ratio = this._adjustScene.getRatio();
      const rulerWidthInMM = (((canvas.width / canvasRenderScale) * ratio) / 72) * 25.4;
      const rulerValue = this._adjustScene.getInputValue();
      scaleValue = rulerValue / rulerWidthInMM;
    }
    const entity = this._entity as FloorPlanPdfEntity;
    entity.setCanvas(canvas, scaleValue);
    await entity.render();

    this._setExisting(true);

    return scaleValue;
  };

  public getPdfPreviewCanvasEl = async (file: File) => {
    const canvas = await this.getPdfCanvas(file);
    const previewScene = new PDFFloorPlanPreviewScene();
    this._adjustScene = previewScene;

    previewScene.addCanvasMesh(canvas);

    return previewScene;
  };

  public getPdfCanvas = async (file: File) => {
    const pdfJs = await import('pdfjs-dist');
    pdfJs.GlobalWorkerOptions.workerSrc = pdfJsWorkerUrl;
    const buffer = await file.arrayBuffer();
    const pdfDoc = await pdfJs.getDocument({ data: buffer }).promise;
    const page = await pdfDoc.getPage(1);

    const canvas = document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');
    if (!canvasContext) {
      throw new Error('Failed to create PDF floor plan canvas context');
    }

    const canvasRenderScale = 3;
    const viewport = page.getViewport({ scale: canvasRenderScale });

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.dataset.pdfRenderScale = `${canvasRenderScale}`;

    const renderContext = { canvas, canvasContext, viewport };
    await page.render(renderContext).promise;

    // 释放 PDF.js 资源
    void pdfDoc.destroy();
    page.cleanup(true);

    return canvas;
  };

  /**
   * 批量设置多个 DXF layer 的显隐状态。
   * @param updates - key 为图层名称，value 为目标显隐状态
   */
  public setDxfLayersVisible = (updates: Record<string, boolean>) => {
    if (this._entity instanceof FloorPlanDxfEntity) {
      this._entity.setLayersVisible(updates);
      const updatedLayers = this._layerList.map((layer) => ({
        ...layer,
        visible: updates[layer.name] !== undefined ? updates[layer.name] : layer.visible
      }));
      this._setLayers(updatedLayers);
    }
  };

  public getDxfHiddenLayers = () => {
    if (this._entity instanceof FloorPlanDxfEntity) {
      return this._entity.getHiddenLayers();
    }
    return [];
  };

  /**
   * 根据鼠标位置查询当前位于该点的 DXF 图层名称，返回 null 表示没有图层
   * @param mousePos - 归一化鼠标位置坐标
   */
  public getDxfLayerAtPoint = (mousePos: IPoint2d): string | null => {
    if (this._entity instanceof FloorPlanDxfEntity) {
      return this._entity.getLayerAtPoint(mousePos);
    }
    return null;
  };

  /**
   * 设置被 hover 的 DXF layer，并触发相关事件通知 UI 更新高亮显示
   * @param layerName - 被 hover 的图层名称，null 表示没有图层
   */
  public setDxfLayerHover = (layerName: string | null): void => {
    if (this._entity instanceof FloorPlanDxfEntity) {
      if (this._hoverLayer === layerName) {
        // 避免重复设置同一图层导致的性能问题
        return;
      }
      this._hoverLayer = layerName;
      this._entity.setLayerHighlight(layerName);
    }
  };

  public clickHoveredLayer = (): void => {
    if (this._hoverLayer) {
      this._event.emit(EFloorPlanEventName.LAYER_CLICK, this._hoverLayer);
    }
  };

  public getBoundingBox = () => {
    return this._entity?.getBoundingBox() ?? new Box3();
  };

  public setPdfMeasureMode = (mode: 'ruler' | 'factor') => {
    if (this._adjustScene) {
      this._adjustScene.setMeasureMode(mode);
    }
  };

  /**
   * 释放 PDF 预览场景资源，避免内存泄漏
   */
  public disposePreviewScene = () => {
    if (this._adjustScene) {
      this._adjustScene.dispose();
      this._adjustScene = null;
    }
  };

  public getNearestLine = (x: number, y: number, radius: number) => {
    if (globalStores.visibilityStore.visibleState.floorPlan === false) return;
    if (!(this._entity && this._entity instanceof FloorPlanDxfEntity)) return;
    const context = this._entity.getContext();
    if (!context) return;

    const minIndex = context.quadTree.queryPointNearestLine(x, y, radius);
    if (minIndex !== -1) {
      const lineData = context.lineSegArr.slice(minIndex * 4, minIndex * 4 + 4);
      return [lineData[0], lineData[1], lineData[2], lineData[3]];
    }
  };

  public getNearLines = (x: number, y: number, radius: number) => {
    if (globalStores.visibilityStore.visibleState.floorPlan === false) return;
    if (!(this._entity && this._entity instanceof FloorPlanDxfEntity)) return;
    const context = this._entity.getContext();
    if (!context) return;

    const nearLineIndexes = context.quadTree.queryPointNearLines(x, y, radius);
    if (!nearLineIndexes) return;

    const result: number[] = [];
    nearLineIndexes.forEach((index) => {
      result.push(
        context.lineSegArr[index * 4],
        context.lineSegArr[index * 4 + 1],
        context.lineSegArr[index * 4 + 2],
        context.lineSegArr[index * 4 + 3]
      );
    });
    return result;
  };

  public getNearestParallelLineByLine = (start: IPoint2d, end: IPoint2d, maxDistance: number) => {
    if (globalStores.visibilityStore.visibleState.floorPlan === false) return;
    if (!(this._entity && this._entity instanceof FloorPlanDxfEntity)) return;
    const context = this._entity.getContext();
    if (!context) return;

    const nearLineIndex = context.quadTree.querySegmentNearestParallelLine(start, end, maxDistance);
    if (nearLineIndex !== -1) {
      const lineData = context.lineSegArr.slice(nearLineIndex * 4, nearLineIndex * 4 + 4);
      return [lineData[0], lineData[1], lineData[2], lineData[3]];
    }
    return;
  };

  public addListener = (eventName: EFloorPlanEventName, callback: TFloorPlanEvent[typeof eventName]) => {
    this._event.on(eventName, callback);
    return () => {
      this._event.off(eventName, callback);
    };
  };

  public removeListener = (eventName: EFloorPlanEventName, callback: TFloorPlanEvent[typeof eventName]) => {
    this._event.off(eventName, callback);
  };

  public remove = () => {
    if (this._entity) {
      this._entity.dispose();
      this._entity = null;
    }
    this._setExisting(false);
    this._setLayers([]);
    // 底图已移除，把开关复位为可见 —— 否则残留的隐藏状态会让「显示全部」一直处于可点击状态
  };
}
